'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type ColorMode,
  type Edge,
  type FitViewOptions,
  type Node,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import { GRAPH_COLORS } from '../flow-layout';
import {
  isAttackPathOverlayRole,
  type AttackPathEventEdgeData,
  type AttackPathEventNodeData,
} from './attack-path-event-node.types';
import type { AttackPathEventColumnTick } from './build-attack-path-event-graph.util';
import {
  campaignAtFlowPoint,
  overlayAtFlowPoint,
} from './attack-path-campaign-hit.util';
import {
  AttackPathCampaignHoverProvider,
  EMPTY_ATTACK_PATH_CAMPAIGN_HOVER,
} from './attack-path-campaign-hover-context';
import { resolveCampaignHoverBrightNodeIds } from './attack-path-campaign-hover.util';
import {
  AttackPathPopoverLayerContext,
  type AttackPathPopoverLayer,
} from './attack-path-popover-layer-context';
import { resolveAttackPathEventNodeColor } from './attack-path-event-visuals';
import {
  attackPathEventEdgeTypes,
  attackPathEventNodeTypes,
} from './InternalNetworkAttackPathEventNode';
import {
  AttackPathMotionStyles,
  AttackPathTimeAxis,
  ATTACK_PATH_FIT_VIEW_OPTIONS,
  FitViewOnLayoutKey,
  PresentKenBurns,
} from './InternalNetworkAttackPathFlowChrome';
import {
  isSeedRole,
  resolveMaxContentColumn,
  resolveRevealActiveCampaignIds,
} from './attack-path-reveal-theater.util';
import {
  decorateAttackPathFlowEdges,
  decorateAttackPathFlowNodes,
} from './attack-path-flow-elements.util';
import { useAttackPathRevealBeats } from './useAttackPathRevealBeats';

type InternalNetworkAttackPathEventFlowProps = {
  nodes: Node<AttackPathEventNodeData>[];
  edges: Edge<AttackPathEventEdgeData>[];
  columns: AttackPathEventColumnTick[];
  revealUpToSeconds?: number;
  revealProgress?: number;
  revealUpToColumn?: number;
  selectedNodeId?: string | null;
  focusBrightNodeIds?: ReadonlySet<string> | null;
  onNodeClick?: NodeMouseHandler<Node<AttackPathEventNodeData>>;
  onPaneClick?: () => void;
  onClimaxReveal?: () => void;
  hideControls?: boolean;
  showMiniMap?: boolean;
  minFitZoom?: number;
  showTimeAxis?: boolean;
  cinematic?: boolean;
  fitRevision?: string;
  fitViewOptions?: FitViewOptions;
  nodePopoverLayer?: AttackPathPopoverLayer;
};

const DEFAULT_EDGE_OPTIONS = {
  type: 'attackPathEventEdge' as const,
};

const PRO_OPTIONS = { hideAttribution: true };

const miniMapNodeColor = (node: Node<AttackPathEventNodeData>): string =>
  isAttackPathOverlayRole(node.data.role)
    ? 'transparent'
    : resolveAttackPathEventNodeColor(node.data);

const isNodeHidden = ({
  node,
  revealUpToSeconds,
  revealProgress,
  revealUpToColumn,
}: {
  node: Node<AttackPathEventNodeData>;
  revealUpToSeconds?: number;
  revealProgress?: number;
  revealUpToColumn?: number;
}): boolean => {
  if (isAttackPathOverlayRole(node.data.role)) {
    return false;
  }

  if (isSeedRole(node.data.role)) {
    return false;
  }

  if (revealUpToColumn !== undefined) {
    return node.data.column > revealUpToColumn;
  }

  if (revealProgress !== undefined) {
    return node.data.chainProgress > revealProgress;
  }

  if (revealUpToSeconds !== undefined) {
    return node.data.elapsedSeconds > revealUpToSeconds;
  }

  return false;
};

function AttackPathEventFlowInner({
  nodes: sourceNodes,
  edges: sourceEdges,
  columns,
  revealUpToSeconds,
  revealProgress,
  revealUpToColumn,
  selectedNodeId,
  focusBrightNodeIds,
  onNodeClick,
  onPaneClick,
  onClimaxReveal,
  hideControls,
  showMiniMap,
  minFitZoom,
  showTimeAxis = true,
  cinematic,
  fitRevision,
  fitViewOptions = ATTACK_PATH_FIT_VIEW_OPTIONS,
}: InternalNetworkAttackPathEventFlowProps) {
  const { mode: themeMode } = useThemeMode();
  const colorMode: ColorMode = themeMode === 'dark' ? 'dark' : 'light';
  const { screenToFlowPosition } = useReactFlow();
  const [highlightCampaignId, setHighlightCampaignId] = useState<string | null>(
    null,
  );

  const pointFromEvent = (event: { clientX: number; clientY: number }) =>
    screenToFlowPosition({ x: event.clientX, y: event.clientY });
  const campaignAtPointer = (event: { clientX: number; clientY: number }) =>
    campaignAtFlowPoint({
      point: pointFromEvent(event),
      nodes: sourceNodes,
    });
  const overlayAtPointer = (event: { clientX: number; clientY: number }) =>
    overlayAtFlowPoint({
      point: pointFromEvent(event),
      nodes: sourceNodes,
    });

  const isRevealing = revealUpToColumn !== undefined;
  const focusActive =
    !isRevealing && focusBrightNodeIds != null && focusBrightNodeIds.size > 0;

  useEffect(() => {
    if (isRevealing) {
      setHighlightCampaignId(null);
    }
  }, [isRevealing]);

  const campaignHover = useMemo(() => {
    if (focusActive || highlightCampaignId == null) {
      return EMPTY_ATTACK_PATH_CAMPAIGN_HOVER;
    }

    return {
      campaignId: highlightCampaignId,
      brightNodeIds: resolveCampaignHoverBrightNodeIds({
        campaignId: highlightCampaignId,
        nodes: sourceNodes,
        edges: sourceEdges,
      }),
    };
  }, [focusActive, highlightCampaignId, sourceEdges, sourceNodes]);

  const maxContentColumn = useMemo(
    () => resolveMaxContentColumn(sourceNodes),
    [sourceNodes],
  );
  const fitLayoutKey = useMemo(
    () =>
      [
        cinematic ? 'present' : 'widget',
        fitRevision ?? '',
        cinematic
          ? 'reveal'
          : revealUpToColumn === undefined
            ? 'full'
            : `col:${revealUpToColumn}`,
        String(sourceNodes.length),
        ...sourceNodes.map(
          (node) =>
            `${node.id}:${Math.round(node.position.x)}:${Math.round(node.position.y)}`,
        ),
      ].join('|'),
    [cinematic, fitRevision, revealUpToColumn, sourceNodes],
  );
  const revealActiveCampaignIds = useMemo(
    () =>
      resolveRevealActiveCampaignIds({
        nodes: sourceNodes,
        revealUpToColumn,
      }),
    [revealUpToColumn, sourceNodes],
  );

  const contentHiddenById = useMemo(() => {
    const next = new Map<string, boolean>();

    for (const node of sourceNodes) {
      if (isAttackPathOverlayRole(node.data.role)) {
        continue;
      }

      next.set(
        node.id,
        isNodeHidden({
          node,
          revealUpToSeconds,
          revealProgress,
          revealUpToColumn,
        }),
      );
    }

    return next;
  }, [revealProgress, revealUpToColumn, revealUpToSeconds, sourceNodes]);

  const revealFingerprint = useMemo(
    () =>
      [...contentHiddenById.entries()]
        .map(([id, hidden]) => `${id}:${hidden ? 1 : 0}`)
        .join('|'),
    [contentHiddenById],
  );

  const frameHiddenById = useMemo(() => {
    const next = new Map<string, boolean>();

    for (const node of sourceNodes) {
      if (node.data.role !== 'CAMPAIGN_FRAME') {
        continue;
      }

      next.set(
        node.id,
        !sourceNodes.some(
          (candidate) =>
            candidate.data.role !== 'CAMPAIGN_FRAME' &&
            candidate.data.campaignId === node.data.campaignId &&
            !(contentHiddenById.get(candidate.id) ?? true),
        ),
      );
    }

    return next;
  }, [contentHiddenById, sourceNodes]);

  const { climaxNodeIds, drawingEdgeIds, pathInkEdgeIds } =
    useAttackPathRevealBeats({
      contentHiddenById,
      frameHiddenById,
      revealFingerprint,
      isRevealing,
      sourceNodes,
      sourceEdges,
      onClimaxReveal,
    });

  const flowNodes = useMemo(() => {
    return decorateAttackPathFlowNodes({
      sourceNodes,
      sourceEdges,
      frameHiddenById,
      climaxNodeIds,
      maxContentColumn,
      selectedNodeId,
      contentHiddenById,
      pathInkEdgeIds,
      revealActiveCampaignIds,
      revealUpToColumn,
      isRevealing,
      cinematic,
      focusActive,
      focusBrightNodeIds,
    });
  }, [
    cinematic,
    climaxNodeIds,
    contentHiddenById,
    focusActive,
    focusBrightNodeIds,
    frameHiddenById,
    isRevealing,
    maxContentColumn,
    pathInkEdgeIds,
    revealActiveCampaignIds,
    revealFingerprint,
    revealUpToColumn,
    selectedNodeId,
    sourceEdges,
    sourceNodes,
  ]);

  const flowEdges = useMemo(() => {
    return decorateAttackPathFlowEdges({
      sourceNodes,
      sourceEdges,
      drawingEdgeIds,
      contentHiddenById,
      pathInkEdgeIds,
      revealActiveCampaignIds,
      revealUpToColumn,
      isRevealing,
      focusActive,
      focusBrightNodeIds,
    });
  }, [
    contentHiddenById,
    drawingEdgeIds,
    focusActive,
    focusBrightNodeIds,
    isRevealing,
    pathInkEdgeIds,
    revealActiveCampaignIds,
    revealFingerprint,
    revealUpToColumn,
    sourceEdges,
    sourceNodes,
  ]);

  return (
    <Box
      w="full"
      h="full"
      position="relative"
      onMouseMove={(event) => {
        if (isRevealing) {
          return;
        }

        const nextCampaignId = campaignAtPointer(event)?.campaignId ?? null;

        setHighlightCampaignId((current) =>
          current === nextCampaignId ? current : nextCampaignId,
        );
      }}
      onMouseLeave={() => {
        if (isRevealing) {
          return;
        }

        setHighlightCampaignId((current) => (current == null ? current : null));
      }}
    >
      <AttackPathMotionStyles />
      <AttackPathCampaignHoverProvider value={campaignHover}>
        {cinematic ? (
          <Box
            position="absolute"
            inset={0}
            pointerEvents="none"
            zIndex={1}
            bg="bg.canvas"
            opacity={0.28}
            style={{
              maskImage:
                'radial-gradient(ellipse at center, transparent 42%, black 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse at center, transparent 42%, black 100%)',
            }}
          />
        ) : null}
        <ReactFlow<Node<AttackPathEventNodeData>, Edge<AttackPathEventEdgeData>>
          colorMode={colorMode}
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={attackPathEventNodeTypes}
          edgeTypes={attackPathEventEdgeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={(event) => {
            const overlay = overlayAtPointer(event);

            if (overlay) {
              onNodeClick?.(event, overlay);

              return;
            }

            const hit = campaignAtPointer(event);

            if (hit) {
              onNodeClick?.(event, hit.frame);

              return;
            }

            onPaneClick?.();
          }}
          onInit={(instance) => {
            void instance.fitView(fitViewOptions);
            window.setTimeout(() => {
              void instance.fitView(fitViewOptions);
            }, 50);
          }}
          elevateNodesOnSelect={false}
          minZoom={0.1}
          maxZoom={2.5}
          nodesDraggable={false}
          proOptions={PRO_OPTIONS}
          defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
          style={{ width: '100%', height: '100%' }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            color={GRAPH_COLORS.background}
            gap={cinematic ? 18 : 12}
            size={cinematic ? 1.4 : 1}
          />
          <FitViewOnLayoutKey
            layoutKey={fitLayoutKey}
            fitViewOptions={fitViewOptions}
            minFitZoom={minFitZoom}
          />
          <PresentKenBurns
            nodes={flowNodes}
            revealUpToColumn={revealUpToColumn}
            enabled={Boolean(cinematic && isRevealing)}
          />
          {hideControls ? null : (
            <Controls showInteractive={false} fitViewOptions={fitViewOptions} />
          )}
          {showMiniMap ? (
            <MiniMap
              nodeStrokeWidth={0}
              nodeColor={miniMapNodeColor}
              zoomable
              pannable
              position="bottom-right"
            />
          ) : null}
          {showTimeAxis ? <AttackPathTimeAxis columns={columns} /> : null}
        </ReactFlow>
      </AttackPathCampaignHoverProvider>
    </Box>
  );
}

export function InternalNetworkAttackPathEventFlow({
  nodePopoverLayer = 'flow',
  ...props
}: InternalNetworkAttackPathEventFlowProps) {
  return (
    <ReactFlowProvider>
      <AttackPathPopoverLayerContext.Provider value={nodePopoverLayer}>
        <AttackPathEventFlowInner {...props} />
      </AttackPathPopoverLayerContext.Provider>
    </ReactFlowProvider>
  );
}
